import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EllipsisIcon, PackageIcon, PencilIcon, PlusIcon, XIcon } from 'lucide-react';
import { Button, CardHeader, Combobox, ConfirmDialog, EmptyState, ErrorNotice, Field, Panel, SelectInput, TextArea, TextInput } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api';
import { listSupplyItems, ruleText, saveProgramKit, type KitLine, type QuantityRule } from '@/lib/supplies';

/** How a kit line's quantity is worked out. */
const RULE_KIND = {
  fixed: 'fixed',
  perTeam: 'perTeam',
  perPerson: 'perPerson',
  perGroup: 'perGroup',
  ranges: 'ranges',
} as const;
type RuleKind = (typeof RULE_KIND)[keyof typeof RULE_KIND];
const RULE_KIND_LABELS: Readonly<Record<RuleKind, string>> = {
  fixed: 'A fixed number',
  perTeam: 'So many per team',
  perPerson: 'So many per person',
  perGroup: 'So many per group of people',
  ranges: 'By participant range',
};

interface BandDraft {
  readonly id: number;
  readonly upTo: string;
  readonly quantity: string;
}

interface LineDraft {
  readonly id: number;
  readonly itemId: string;
  readonly kind: RuleKind;
  /** The quantity for fixed, per team, per person and per group. */
  readonly quantity: string;
  /** The group size for per group. */
  readonly groupSize: string;
  /** Added on top for every rule but fixed: spares, a demo set. */
  readonly extra: string;
  readonly bands: readonly BandDraft[];
  readonly optional: boolean;
  readonly note: string;
}
let nextId = 1;

function blankBand(): BandDraft {
  return { id: nextId++, upTo: '', quantity: '' };
}
function blankLine(): LineDraft {
  return { id: nextId++, itemId: '', kind: RULE_KIND.fixed, quantity: '', groupSize: '', extra: '', bands: [], optional: false, note: '' };
}

/** A saved rule as the editor shows it: the kind is whichever part is set. */
function draftFrom(line: KitLine): LineDraft {
  const rule = line.quantityRule;
  const text = (value: number | undefined): string => (value ? String(value) : '');
  const base = { id: nextId++, itemId: line.itemId, optional: line.optional ?? false, note: line.note ?? '', groupSize: '', bands: [] as readonly BandDraft[] };
  if (rule.ranges?.length) {
    return { ...base, kind: RULE_KIND.ranges, quantity: '', extra: text(rule.fixed), bands: rule.ranges.map((band) => ({ id: nextId++, upTo: band.upTo === undefined ? '' : String(band.upTo), quantity: String(band.quantity) })) };
  }
  if (rule.perGroup) return { ...base, kind: RULE_KIND.perGroup, quantity: String(rule.perGroup.quantity), groupSize: String(rule.perGroup.participants), extra: text(rule.fixed) };
  if (rule.perParticipant) return { ...base, kind: RULE_KIND.perPerson, quantity: String(rule.perParticipant), extra: text(rule.fixed) };
  if (rule.perTeam) return { ...base, kind: RULE_KIND.perTeam, quantity: String(rule.perTeam), extra: text(rule.fixed) };
  return { ...base, kind: RULE_KIND.fixed, quantity: text(rule.fixed), extra: '' };
}

function num(value: string): number | undefined {
  return value.trim() === '' ? undefined : Number(value);
}

function ruleFrom(draft: LineDraft): QuantityRule {
  const extra = num(draft.extra);
  const withExtra = (rule: QuantityRule): QuantityRule => (extra ? { ...rule, fixed: extra } : rule);
  switch (draft.kind) {
    case RULE_KIND.fixed:
      return { fixed: num(draft.quantity) };
    case RULE_KIND.perTeam:
      return withExtra({ perTeam: num(draft.quantity) });
    case RULE_KIND.perPerson:
      return withExtra({ perParticipant: num(draft.quantity) });
    case RULE_KIND.perGroup:
      return withExtra({ perGroup: { quantity: num(draft.quantity) ?? 0, participants: num(draft.groupSize) ?? 0 } });
    case RULE_KIND.ranges:
      return withExtra({
        ranges: draft.bands.map((band) => ({ ...(num(band.upTo) === undefined ? {} : { upTo: num(band.upTo) }), quantity: num(band.quantity) ?? 0 })),
      });
    default: {
      const never: never = draft.kind;
      return never;
    }
  }
}

function lineFrom(draft: LineDraft): KitLine | null {
  if (!draft.itemId) return null;
  return { itemId: draft.itemId, quantityRule: ruleFrom(draft), optional: draft.optional, note: draft.note.trim() || undefined };
}

const positive = (value: string): boolean => value.trim() !== '' && Number(value) > 0;

function lineValid(draft: LineDraft): boolean {
  if (!draft.itemId) return false;
  switch (draft.kind) {
    case RULE_KIND.fixed:
    case RULE_KIND.perTeam:
    case RULE_KIND.perPerson:
      return positive(draft.quantity);
    case RULE_KIND.perGroup:
      return positive(draft.quantity) && positive(draft.groupSize);
    case RULE_KIND.ranges:
      return draft.bands.length > 0 && draft.bands.every((band) => band.quantity.trim() !== '' && Number(band.quantity) >= 0 && (band.upTo.trim() === '' || Number(band.upTo) > 0));
    default: {
      const never: never = draft.kind;
      return never;
    }
  }
}

/**
 * A program's default kit: catalog items with a quantity rule each (a fixed
 * number, per team, per person, per group of people, or the line's own
 * participant ranges). Events materialise it into their packing list. One
 * item is added or edited at a time; each save writes the whole kit.
 * Internal roles edit.
 */
export function ProgramKitPanel({ programId, kit, canEdit }: { readonly programId: string; readonly kit: readonly KitLine[]; readonly canEdit: boolean }): React.ReactElement {
  const queryClient = useQueryClient();
  const catalog = useQuery({ queryKey: ['supplies'], queryFn: ({ signal }) => listSupplyItems(signal), staleTime: 60_000 });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  const nameOf = (itemId: string): string => catalog.data?.find((item) => item._id === itemId)?.name ?? 'Item no longer in the catalog';

  async function saveKit(next: readonly KitLine[]): Promise<void> {
    await saveProgramKit(programId, next);
    await queryClient.invalidateQueries({ queryKey: ['program', programId] });
  }
  const remove = useMutation({
    mutationFn: (index: number) => saveKit(kit.filter((_line, at) => at !== index)),
    onSuccess: (_result, index) => {
      toast.success(`Removed ${nameOf(kit[index]?.itemId ?? '')} from the kit`);
      setRemoving(null);
    },
  });

  return (
    <Panel className="mt-4 p-5">
      <div data-guide="kit-header"><CardHeader
        title="Kit"
        description="What ships for this program. Quantities follow the event's participants and teams."
        aside={
          canEdit && kit.length > 0 ? (
            <span data-guide="kit-add">
              <Button variant="secondary" size="compact" onClick={() => setAdding(true)}>
                <PlusIcon aria-hidden className="size-4" /> Add item
              </Button>
            </span>
          ) : undefined
        }
      /></div>

      {kit.length === 0 ? (
        <EmptyState icon={<PackageIcon aria-hidden className="size-6" />} title="No kit yet" message="Add the catalog items this program needs and how many each event gets." {...(canEdit ? { action: <Button onClick={() => setAdding(true)}>Add item</Button> } : {})} />
      ) : (
        <ul data-guide="kit-list" className="divide-y divide-portal-line">
          {kit.map((line, index) => (
            <li key={`${line.itemId}-${index}`} className="flex items-start justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
              <span className="min-w-0">
                <span className="block text-portal-small font-bold">{nameOf(line.itemId)}</span>
                <span className="block text-portal-micro text-portal-muted">
                  {ruleText(line.quantityRule)}
                  {line.optional ? ' · optional' : ''}
                  {line.note ? ` · ${line.note}` : ''}
                </span>
              </span>
              {canEdit ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" data-guide="kit-row-menu" aria-label={`Actions for ${nameOf(line.itemId)}`} className="grid size-7 shrink-0 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink">
                      <EllipsisIcon aria-hidden className="size-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => setEditing(index)}>
                      <PencilIcon aria-hidden className="size-4" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive" onSelect={() => setRemoving(index)}>
                      <XIcon aria-hidden className="size-4" /> Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {adding ? <KitLineDialog onClose={() => setAdding(false)} onSave={(line) => saveKit([...kit, line])} /> : null}
      {editing !== null && kit[editing] ? (
        <KitLineDialog
          key={editing}
          existing={kit[editing]}
          onClose={() => setEditing(null)}
          onSave={(line) => saveKit(kit.map((row, at) => (at === editing ? line : row)))}
        />
      ) : null}
      <ConfirmDialog
        open={removing !== null}
        tone="danger"
        title={`Remove ${removing === null ? 'this item' : nameOf(kit[removing]?.itemId ?? '')} from the kit?`}
        message="Packing lists already built keep their copy; the next event's list will not have it."
        confirmLabel="Remove"
        pending={remove.isPending}
        error={remove.isError ? errorMessage(remove.error) : undefined}
        onConfirm={() => {
          if (removing !== null) remove.mutate(removing);
        }}
        onCancel={() => {
          if (!remove.isPending) setRemoving(null);
        }}
      />
    </Panel>
  );
}

/** One kit line: the item, how its quantity follows the event, and a note. */
function KitLineDialog({ existing, onClose, onSave }: { readonly existing?: KitLine; readonly onClose: () => void; readonly onSave: (line: KitLine) => Promise<void> }): React.ReactElement {
  const catalog = useQuery({ queryKey: ['supplies'], queryFn: ({ signal }) => listSupplyItems(signal), staleTime: 60_000 });
  const [draft, setDraft] = useState<LineDraft>(existing ? draftFrom(existing) : blankLine());
  const update = (patch: Partial<LineDraft>): void => setDraft((current) => ({ ...current, ...patch }));
  const updateBand = (bandId: number, patch: Partial<BandDraft>): void => setDraft((current) => ({ ...current, bands: current.bands.map((band) => (band.id === bandId ? { ...band, ...patch } : band)) }));
  const save = useMutation({
    mutationFn: () => {
      const line = lineFrom(draft);
      if (!line) throw new Error('Pick an item.');
      return onSave(line);
    },
    onSuccess: () => {
      toast.success(existing ? 'Kit line saved' : 'Added to the kit');
      onClose();
    },
  });
  const valid = lineValid(draft) && !save.isPending;
  const itemName = catalog.data?.find((item) => item._id === existing?.itemId)?.name;

  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-xl">
        <DialogTitle>{existing ? `Edit ${itemName ?? 'kit item'}` : 'Add to the kit'}</DialogTitle>
        <p className="text-portal-small text-portal-muted">The item and how many of it an event gets.</p>
        <form
          className="mt-4 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <Field guide="kit-item" label="Item" htmlFor="kit-item" required hint="From the supplies catalog.">
            <Combobox
              id="kit-item"
              options={(catalog.data ?? []).filter((item) => item.isActive || item._id === draft.itemId).map((item) => ({ value: item._id, label: item.name }))}
              value={draft.itemId}
              onChange={(value) => update({ itemId: value })}
              placeholder="Search the catalog"
              noneLabel="Pick an item"
            />
          </Field>
          <Field guide="kit-how-many" label="How many" htmlFor="kit-kind" required hint="How the quantity follows the event.">
            <SelectInput id="kit-kind" value={draft.kind} onChange={(event) => update({ kind: event.target.value as RuleKind })}>
              {(Object.keys(RULE_KIND_LABELS) as RuleKind[]).map((kind) => (
                <option key={kind} value={kind}>
                  {RULE_KIND_LABELS[kind]}
                </option>
              ))}
            </SelectInput>
          </Field>
          <div className="grid gap-4 sm:grid-cols-3">
            {draft.kind === RULE_KIND.fixed ? (
              <Field guide="kit-quantity" label="Quantity" htmlFor="kit-qty" required hint="The same for every event.">
                <TextInput id="kit-qty" type="number" min={0} value={draft.quantity} onChange={(event) => update({ quantity: event.target.value })} />
              </Field>
            ) : null}
            {draft.kind === RULE_KIND.perTeam ? (
              <Field guide="kit-per-team" label="Per team" htmlFor="kit-qty" required hint="Times the team count.">
                <TextInput id="kit-qty" type="number" min={0} step="0.5" value={draft.quantity} onChange={(event) => update({ quantity: event.target.value })} />
              </Field>
            ) : null}
            {draft.kind === RULE_KIND.perPerson ? (
              <Field guide="kit-per-person" label="Per person" htmlFor="kit-qty" required hint="Times the participants.">
                <TextInput id="kit-qty" type="number" min={0} step="0.5" value={draft.quantity} onChange={(event) => update({ quantity: event.target.value })} />
              </Field>
            ) : null}
            {draft.kind === RULE_KIND.perGroup ? (
              <>
                <Field label="Quantity" htmlFor="kit-qty" required hint="For every group.">
                  <TextInput id="kit-qty" type="number" min={0} value={draft.quantity} onChange={(event) => update({ quantity: event.target.value })} />
                </Field>
                <Field guide="kit-for-every" label="For every" htmlFor="kit-group" required hint="People per group; a part group counts as one.">
                  <TextInput id="kit-group" type="number" min={1} value={draft.groupSize} placeholder="25" onChange={(event) => update({ groupSize: event.target.value })} />
                </Field>
              </>
            ) : null}
            {draft.kind !== RULE_KIND.fixed ? (
              <Field guide="kit-plus" label="Plus" htmlFor="kit-extra" hint="Added on top: spares, a demo set.">
                <TextInput id="kit-extra" type="number" min={0} value={draft.extra} onChange={(event) => update({ extra: event.target.value })} />
              </Field>
            ) : null}
          </div>
          {draft.kind === RULE_KIND.ranges ? (
            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <span data-guide="kit-ranges" className="text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">Participant ranges</span>
                <Button guide="kit-add-range" type="button" variant="secondary" size="compact" onClick={() => update({ bands: [...draft.bands, blankBand()] })}>
                  <PlusIcon aria-hidden className="size-4" /> Add range
                </Button>
              </div>
              {draft.bands.length === 0 ? (
                <p className="text-portal-small text-portal-muted">Add a range for each size of event: up to 50 people, up to 100, and so on. Leave the last one open for anything bigger.</p>
              ) : (
                <div className="space-y-3">
                  {draft.bands.map((band, index) => (
                    <div key={band.id} className="grid items-start gap-4 sm:grid-cols-[1fr_1fr_auto]">
                      <Field guide="kit-up-to" label="Up to" htmlFor={`kit-band-upto-${band.id}`} hint={index === draft.bands.length - 1 ? 'People, inclusive. Leave empty for "and above".' : 'People, inclusive.'}>
                        <TextInput id={`kit-band-upto-${band.id}`} type="number" min={1} value={band.upTo} placeholder="and above" onChange={(event) => updateBand(band.id, { upTo: event.target.value })} />
                      </Field>
                      <Field label="Quantity" htmlFor={`kit-band-qty-${band.id}`} required hint="For events in this range.">
                        <TextInput id={`kit-band-qty-${band.id}`} type="number" min={0} value={band.quantity} onChange={(event) => updateBand(band.id, { quantity: event.target.value })} />
                      </Field>
                      <button type="button" aria-label={`Remove range ${index + 1}`} onClick={() => update({ bands: draft.bands.filter((row) => row.id !== band.id) })} className="mt-6 grid size-8 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-rose-600">
                        <XIcon aria-hidden className="size-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
          <Field guide="kit-note" label="Note" htmlFor="kit-note" hint="Shown on every packing list built from this kit.">
            <TextArea id="kit-note" rows={3} value={draft.note} maxLength={300} placeholder="11 Gold, 11 Silver, 11 Bronze" onChange={(event) => update({ note: event.target.value })} />
          </Field>
          <label data-guide="kit-optional" className="flex items-center gap-2 text-portal-small">
            <input type="checkbox" checked={draft.optional} onChange={(event) => update({ optional: event.target.checked })} />
            Optional: pack it when there is room
          </label>
          {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid}>
              {save.isPending ? 'Saving…' : existing ? 'Save' : 'Add to kit'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
